import { useCallback } from 'react'
import { runGraphRecalculation } from '../utils/recalculateGraphFlow'
import { useZustand } from './useZustand'
import { useGraphStore } from '../stores/useGraphStore'
import { useFilterStore, emptyState } from '../stores/useFilterStore'
import { useBootstrapStore } from '../stores/useBootstrapStore'
import { useFindStore } from '../stores/useFindStore'
import { VIEW_TYPES } from '../utils/viewType'

/**
 * Apply a recalculated graph as the new baseline + refresh AI topic names after
 * a statement mutation. Mutations pass their endpoint's graph as
 * precomputedGraphData to skip the refetch.
 */
export function useRecalculateCurrentGraph() {
    return useCallback(async ({ precomputedGraphData = null } = {}) => {
        const currentContext = useZustand.getState().currentContext
        const currentStatements =
            useZustand.getState().statements?.[currentContext] || []

        // isSearchView/searchTerms aren't bootstrap fields — derive them like
        // setRecalculate does.
        const {
            synthesisType,
            viewType,
            isPublic,
            graphOwnerUsername,
        } = useBootstrapStore.getState()
        const isSearchView = viewType === VIEW_TYPES.SEARCH
        const searchTerms = isSearchView
            ? useFindStore.getState().terms
            : null
        const stopWords = useFilterStore.getState().stopWords || []

        if (useGraphStore.getState().isRecalculating) {
            console.warn(
                '[useRecalculateCurrentGraph] baseline recalc started while ' +
                    'another recalc is in flight — possible state race (R3).'
            )
        }

        const { onGraphData, onTopicsApplied } = useFilterStore
            .getState()
            .createRecalculationCallbacks({
                newState: emptyState,
                statementWithUpdatedCommunities: currentStatements,
                asBaseline: true,
            })

        const additionalContexts =
            useZustand.getState().additionalContexts || []
        const comparisonMode = useZustand.getState().currentCompareMode

        try {
            useGraphStore.getState().setRecalculating(true)
            await runGraphRecalculation({
                contextName:
                    synthesisType || isSearchView ? null : currentContext,
                filteredStatementIds: [],
                stopWords,
                userName: isPublic ? graphOwnerUsername : null,
                synthesisType,
                searchTerms,
                additionalContexts,
                comparisonMode,
                precomputedGraphData,
                onGraphData,
                onTopicsApplied,
            })
        } finally {
            useGraphStore.getState().setRecalculating(false)
        }
    }, [])
}
